import { useEffect, useState } from "react";
import { useBilingual } from "@/lib/bilingual";

interface ImageGalleryProps {
  images: string[];
  name: string;
}

const ImageGallery = ({ images, name }: ImageGalleryProps) => {
  const b = useBilingual();
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  // Defensive reset whenever the product's image set changes — ensures
  // navigating between SKUs never leaves the previous image visible even
  // if the parent key-based remount is deferred (StrictMode / HMR quirks).
  const imagesKey = images.join("|");
  useEffect(() => {
    setActive(0);
    setLightbox(false);
  }, [imagesKey]);

  const total = images.length;
  const next = () => setActive((i) => (i + 1) % total);
  const prev = () => setActive((i) => (i - 1 + total) % total);

  // Keyboard navigation in lightbox.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, total]);

  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Main image */}
        <div
          className="relative bg-stone-50 rounded-2xl overflow-hidden cursor-zoom-in group"
          style={{ height: "460px" }}
          onClick={() => setLightbox(true)}
        >
          <img
            src={images[active]}
            alt={name}
            className="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105"
          />
          {total > 1 && (
            <>
              <button
                type="button"
                aria-label={b("Previous image", "上一張")}
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-stone-700 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              <button
                type="button"
                aria-label={b("Next image", "下一張")}
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-stone-700 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </>
          )}
          <div className="absolute top-4 right-4 flex items-center gap-2">
            <span className="bg-white/80 backdrop-blur-sm text-stone-700 text-xs font-semibold px-3 py-1.5 rounded-full">
              {active + 1}/{total}
            </span>
            <button
              type="button"
              aria-label={b("Open lightbox", "全螢幕檢視")}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-stone-600 hover:text-stone-900 transition-colors cursor-pointer"
            >
              <i className="ri-zoom-in-line text-sm" />
            </button>
          </div>
        </div>

        {/* Thumbnails */}
        {total > 1 && (
          <div className="flex gap-3 flex-wrap">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                aria-label={b(`View image ${i + 1}`, `檢視第 ${i + 1} 張`)}
                className={`relative rounded-xl overflow-hidden flex-shrink-0 transition-all duration-200 cursor-pointer ${
                  active === i ? "ring-2 ring-amber-700 ring-offset-2" : "opacity-60 hover:opacity-100"
                }`}
                style={{ width: "80px", height: "80px" }}
              >
                <img
                  src={img}
                  alt={`${name} · ${i + 1}`}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-6"
          onClick={() => setLightbox(false)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            aria-label={b("Close", "關閉")}
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox(false);
            }}
          >
            <i className="ri-close-line text-xl" />
          </button>
          {/* Prev arrow — pinned to the viewport edge */}
          {total > 1 && (
            <button
              type="button"
              aria-label={b("Previous image", "上一張")}
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-2xl" />
            </button>
          )}

          {/* Image — scales to the viewport, keeps aspect ratio, centered */}
          <img
            src={images[active]}
            alt={name}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[85vh] object-contain rounded-lg select-none"
          />

          {/* Next arrow — pinned to the viewport edge */}
          {total > 1 && (
            <button
              type="button"
              aria-label={b("Next image", "下一張")}
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-2xl" />
            </button>
          )}

          {total > 1 && (
            <div
              className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 flex gap-2 bg-black/40 backdrop-blur-sm rounded-full px-3 py-2"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`rounded-lg overflow-hidden transition-all cursor-pointer ${
                    active === i
                      ? "ring-2 ring-amber-500 ring-offset-1 ring-offset-black"
                      : "opacity-50 hover:opacity-80"
                  }`}
                  style={{ width: "52px", height: "52px" }}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ImageGallery;
